import {
  JOURNAL_ENTRY_STATUSES,
  JOURNAL_ENTRY_TYPES,
  type JournalEntrySummary,
  journalEntryStatusSchema,
  journalEntryTypeSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BookOpenText, Plus } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountingPage, companySearchShape } from "../../../../features/accounting/company.js";
import { formatMinor } from "../../../../features/accounting/entry-math.js";
import { EntryStatusBadge, JOURNAL_SOURCES } from "../../../../features/accounting/shared.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDate } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";
import { usePermissions } from "../../../../lib/session.js";

const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  type: journalEntryTypeSchema.optional().catch(undefined),
  status: journalEntryStatusSchema.optional().catch(undefined),
  source: z.string().max(40).optional().catch(undefined),
  from: dateSchema,
  to: dateSchema,
  account: z.string().uuid().optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/muhasebe/fisler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: EntriesPage,
});

function EntriesPage() {
  const { t } = useTranslation();
  const canManage = usePermissions().has("accounting.entry.manage");
  return (
    <AccountingPage
      title={t("menu.journalEntries")}
      description={t("accounting.entries.description")}
      actions={
        canManage ? (
          <Button asChild>
            <Link to="/muhasebe/fisler/yeni">
              <Plus aria-hidden="true" />
              {t("accounting.entries.new")}
            </Link>
          </Button>
        ) : null
      }
    >
      {({ companyId }) => <EntryList companyId={companyId} />}
    </AccountingPage>
  );
}

function EntryList({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const { company: _company, ...filters } = Route.useSearch();
  const [searchText, setSearchText] = useState(filters.q ?? "");
  const pages = useCursorPages(JSON.stringify([companyId, filters]));
  const setFilters = (patch: Partial<typeof filters>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((filters.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const query = {
    companyId,
    limit: 25,
    ...(filters.type ? { entryType: filters.type } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.source ? { source: filters.source } : {}),
    ...(filters.from ? { dateFrom: filters.from } : {}),
    ...(filters.to ? { dateTo: filters.to } : {}),
    ...(filters.account ? { accountId: filters.account } : {}),
    ...(filters.q ? { search: filters.q } : {}),
  };
  const entries = useQuery({
    queryKey: queryKeys.accounting(companyId, "entries", { ...query, cursor: pages.cursor ?? null }),
    queryFn: async () =>
      (
        await api.GET("/accounting/journal-entries", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<DataTableColumn<JournalEntrySummary>[]>(
    () => [
      {
        id: "entryNo",
        header: t("accounting.fields.entryNo"),
        hideable: false,
        cell: (row) => (
          // Satırın tamamı tıklanabilir; bağlantı klavye ve ekran okuyucu için gerçek hedeftir.
          <Link
            to="/muhasebe/fisler/$id"
            params={{ id: row.id }}
            className="font-mono font-medium text-primary underline-offset-4 hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            {row.entryNo ?? t("accounting.entries.noEntryNo")}
          </Link>
        ),
        exportValue: (row) => row.entryNo,
      },
      {
        id: "postingDate",
        header: t("accounting.fields.postingDate"),
        cell: (row) => <span className="tabular">{formatDate(row.postingDate)}</span>,
        exportValue: (row) => formatDate(row.postingDate),
      },
      {
        id: "documentDate",
        header: t("accounting.fields.documentDate"),
        defaultVisible: false,
        cell: (row) => <span className="tabular">{formatDate(row.documentDate)}</span>,
        exportValue: (row) => formatDate(row.documentDate),
      },
      {
        id: "entryType",
        header: t("accounting.fields.entryType"),
        cell: (row) => t(`enums.journalEntryType.${row.entryType}`),
        exportValue: (row) => t(`enums.journalEntryType.${row.entryType}`),
      },
      {
        id: "description",
        header: t("accounting.fields.description"),
        cell: (row) => <span className="line-clamp-2 min-w-48">{row.description}</span>,
        exportValue: (row) => row.description,
      },
      {
        id: "source",
        header: t("accounting.fields.source"),
        cell: (row) => t(`enums.journalSource.${row.source}`, { defaultValue: row.source }),
        exportValue: (row) => t(`enums.journalSource.${row.source}`, { defaultValue: row.source }),
      },
      {
        id: "lineCount",
        header: t("accounting.fields.lineCount"),
        align: "right",
        defaultVisible: false,
        cell: (row) => <span className="tabular">{row.lineCount}</span>,
        exportValue: (row) => row.lineCount,
      },
      {
        id: "total",
        header: t("accounting.fields.total"),
        align: "right",
        cell: (row) => <MoneyText value={row.totalDebit} className="font-medium" />,
        // Tutar Excel'e METİN olarak yazılır; kuruş dizgesi float'a çevrilmez.
        exportValue: (row) => formatMinor(row.totalDebit),
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => <EntryStatusBadge status={row.status} />,
        exportValue: (row) => t(`enums.journalEntryStatus.${row.status}`),
      },
      {
        id: "createdBy",
        header: t("accounting.fields.createdBy"),
        defaultVisible: false,
        cell: (row) => row.createdByName ?? "—",
        exportValue: (row) => row.createdByName,
      },
    ],
    [t],
  );
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <DataTable
      caption={t("menu.journalEntries")}
      columns={columns}
      rows={entries.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={entries.isPending}
      error={entries.error ? <Alert tone="danger">{errorMessage(entries.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          icon={<BookOpenText />}
          title={hasFilters ? t("accounting.entries.emptyFiltered") : t("accounting.entries.empty")}
          description={
            hasFilters ? t("accounting.entries.emptyFilteredHint") : t("accounting.entries.emptyHint")
          }
        />
      }
      filters={
        <>
          <FormField label={t("common.search")} className="w-52">
            {(control) => (
              <Input
                {...control}
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={t("accounting.entries.searchPlaceholder")}
              />
            )}
          </FormField>
          <FormField label={t("accounting.fields.entryType")} className="w-36">
            {(control) => (
              <Select
                {...control}
                value={filters.type ?? ""}
                onChange={(event) =>
                  setFilters({ type: (event.target.value || undefined) as typeof filters.type })
                }
              >
                <option value="">{t("common.all")}</option>
                {JOURNAL_ENTRY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`enums.journalEntryType.${type}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("common.status")} className="w-36">
            {(control) => (
              <Select
                {...control}
                value={filters.status ?? ""}
                onChange={(event) =>
                  setFilters({ status: (event.target.value || undefined) as typeof filters.status })
                }
              >
                <option value="">{t("common.all")}</option>
                {JOURNAL_ENTRY_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`enums.journalEntryStatus.${status}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("accounting.fields.source")} className="w-40">
            {(control) => (
              <Select
                {...control}
                value={filters.source ?? ""}
                onChange={(event) => setFilters({ source: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {JOURNAL_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {t(`enums.journalSource.${source}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("accounting.fields.dateFrom")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={filters.from ?? null}
                onValueChange={(from) => setFilters({ from: from ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("accounting.fields.dateTo")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={filters.to ?? null}
                onValueChange={(to) => setFilters({ to: to ?? undefined })}
              />
            )}
          </FormField>
          {hasFilters ? (
            <Button
              variant="ghost"
              size="sm"
              className="mb-1"
              onClick={() => {
                setSearchText("");
                void navigate({ search: (previous) => ({ company: previous.company }), replace: true });
              }}
            >
              {t("common.clearFilters")}
            </Button>
          ) : null}
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: entries.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(entries.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
      onExport={async (visible) => {
        const rows = await fetchAllPages(
          async (cursor) =>
            (
              await api.GET("/accounting/journal-entries", {
                params: { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
              })
            ).data!,
        );
        await exportRowsToExcel({
          fileName: "muhasebe-fisleri",
          sheetName: t("menu.journalEntries"),
          columns: visible,
          rows,
        });
      }}
      onRowClick={(row) => void navigate({ to: "/muhasebe/fisler/$id", params: { id: row.id } })}
    />
  );
}
